import React, { useState } from 'react';
import { MegaphoneIcon } from 'lucide-react';
import { useAppState } from '../../contexts/AppStateContext';
import { DEMO_DATE } from '../../data/demo';
import { TaskTemplate } from '../../types/family';
import { dutyScore } from '../../utils/duties';
import { equalShares } from '../../utils/progress';
import { DutyDots } from './DutyDots';
const quickReports = ['Danes urejeno', 'Danes ni bilo treba'];

/** Otrokova tedenska zadolžitev: skrb ves teden, starš pregleda pon, sre in sob. */
export function DutyCard({
  template
}: {
  template: TaskTemplate;
}) {
  const {
    viewer,
    memberById,
    dutyChecksOf,
    dutyReportsOf,
    reportDuty
  } = useAppState();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const checks = dutyChecksOf(template.id);
  const share = equalShares(template.baseValue ?? 0, template.assigneeIds)[viewer.id] ?? 0;
  const score = dutyScore(share, checks);
  const room = template.planKey === 'soba';
  const others = template.assigneeIds.filter(id => id !== viewer.id).map(id => memberById(id)?.name).filter(Boolean);
  const todayReport = dutyReportsOf(template.id).filter(r => r.memberId === viewer.id && r.date === DEMO_DATE).pop();
  const fix = checks.filter(c => c.result === 'ni-urejeno' && !c.fixed).pop();
  const send = (t: string) => {
    reportDuty(template.id, t);
    setOpen(false);
    setText('');
  };
  return <li className="px-4 py-3" data-id="element-98" data-mp="24ymm6i8i6yt6">
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2" data-id="element-99" data-mp="24xuo90rfoozq">
        <div className="min-w-0" data-id="element-100" data-mp="2002yode9heyp">
          <p className="font-bold" data-id="element-101" data-mp="1zwnh93x47tu4">
            {room ? 'Moja soba' : template.title}
            {others.length > 0 && <span className="font-semibold text-muted" data-id="element-102" data-mp="29wo5935xiy4n"> · z {others.join(', ')}</span>}
          </p>
          {template.description && <p className="text-xs text-muted" data-id="element-103" data-mp="1m6bod2sphr1x">Urejeno pomeni: {template.description}</p>}
        </div>
        <span className="shrink-0 rounded-full bg-canvas px-2 py-0.5 text-xs font-extrabold tabular-nums text-muted" data-id="element-104" data-mp="1h8s8jygd167v">
          {score.earned}/{share}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-3" data-id="element-105" data-mp="3m932sacgje3v">
        <DutyDots checks={checks} data-id="element-106" data-mp="3m7i54buujf1s" />
        <span className="text-xs font-semibold text-muted" data-id="element-107" data-mp="3r0be5bxcl9jo">
          {score.checked === 0 ? 'v teku' : `v teku · ${score.tidy}/${score.checked} urejeno`}
        </span>
      </div>

      {fix?.note && <p className="mt-2 rounded-xl bg-accent-soft px-3 py-2 text-xs font-semibold text-accent-ink" data-id="element-108" data-mp="2ybxd0naypx3w">Starš: »{fix.note}« – popravi in povej staršu.</p>}

      <div className="mt-2" data-id="element-109" data-mp="33cl396rda9x2">
        {todayReport && !open ? <p className="text-xs font-semibold text-done-ink" data-id="element-110" data-mp="37bap7nqh0zsb">Danes javljeno: {todayReport.text}</p> : null}
        {open ? <div className="mt-1 flex flex-wrap gap-2" data-id="element-111" data-mp="3c92gqgn7ejvz">
            {quickReports.map(q => <button key={q} type="button" onClick={() => send(q)} className="rounded-full border-[1.5px] border-line bg-surface px-3 py-1.5 text-xs font-bold transition-colors duration-150 hover:bg-canvas" data-id="element-112" data-mp="mnxrxmrm5mc5">
                {q}
              </button>)}
            <form className="flex min-w-[12rem] flex-1 gap-2" onSubmit={e => {
          e.preventDefault();
          if (text.trim()) send(text.trim());
        }} data-id="element-113" data-mp="ryjrlmop56lm">
              <input className="field h-9 flex-1 text-sm" aria-label="Kaj si naredil(a)?" placeholder="npr. zložil(a) perilo" value={text} onChange={e => setText(e.target.value)} data-id="element-114" data-mp="iz6gyqou6bzb" />
              <button type="submit" disabled={!text.trim()} className="rounded-full bg-ink px-3 text-xs font-bold text-white disabled:opacity-40" data-id="element-115" data-mp="918qrxnf2542">
                Javi
              </button>
            </form>
          </div> : <button type="button" onClick={() => setOpen(true)} className="mt-1 inline-flex items-center gap-1.5 text-xs font-extrabold text-info-ink hover:underline" data-id="element-116" data-mp="23c49vtw892l3">
            <MegaphoneIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-117" data-mp="26h4q7nxhtrev" /> Javi stanje
          </button>}
      </div>
    </li>;
}